import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    FaSearch, FaBell, FaUserCircle, FaCalendarAlt, FaMapMarkerAlt,
    FaUser, FaLink, FaDollarSign, FaTag, FaChevronLeft, FaChevronRight,
    FaUsers, FaComments, FaStar, FaTools, FaTimes, FaCheckDouble,
    FaClock, FaExclamationTriangle, FaInfoCircle
} from 'react-icons/fa';
import axios from 'axios';
import './ScientificEvents.css';

// ── MOCK DATA FOR FALLBACK ───────────────────────────────────────────────────
const EVENTS = [
    {
        id: 1,
        type: 'Congreso',
        title: 'Inteligencia Artificial Aplicada a la Biotecnología',
        date: '15 Nov, 2024',
        description: 'Explora los últimos avances en el uso de modelos de lenguaje y redes neuronales para el descubrimiento de fármacos y el análisis genómico.',
        location: 'Madrid, España',
        speaker: 'Dra. Elena Ramos',
        mode: null,
        price: null,
        cupos: null,
        image: 'https://images.unsplash.com/photo-1530026405186-ed1f139313f3?w=400&q=80',
        color: '#FF6600',
    },
    {
        id: 2,
        type: 'Webinar',
        title: 'Microbioma y Salud Planetaria',
        date: '22 Nov, 2024',
        description: 'Sesión virtual sobre la importancia de la diversidad microbiana en el equilibrio de los ecosistemas y su impacto en la salud humana.',
        location: null,
        speaker: null,
        mode: 'Enlace Virtual (Zoom)',
        price: 'Gratuito',
        cupos: null,
        image: 'https://images.unsplash.com/photo-1576086213369-97a306d36557?w=400&q=80',
        color: '#22c55e',
    },
    {
        id: 3,
        type: 'Taller',
        title: 'Materiales Sostenibles para el Futuro',
        date: '05 Dic, 2024',
        description: 'Taller práctico sobre la síntesis de biopolímeros y materiales reciclables de alto rendimiento para la industria verde.',
        location: 'Ciudad de México, MX',
        speaker: null,
        mode: null,
        price: null,
        cupos: 'Cupos Limitados',
        image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=400&q=80',
        color: '#f59e0b',
    },
    {
        id: 4,
        type: 'Simposio',
        title: 'Avances en Oncología de Precisión',
        date: '12 Dic, 2024',
        description: 'Encuentro científico de alto nivel sobre terapias dirigidas, biomarcadores tumorales e inmunoterapia avanzada.',
        location: 'Buenos Aires, Argentina',
        speaker: 'Dr. Martín Varela',
        mode: null,
        price: null,
        cupos: null,
        image: 'https://images.unsplash.com/photo-1559181567-c3190516d669?w=400&q=80',
        color: '#a855f7',
    },
    {
        id: 5,
        type: 'Webinar',
        title: 'Ética en la Investigación Clínica',
        date: '18 Dic, 2024',
        description: 'Debate online sobre estándares internacionales, consentimiento informado y manejo responsable de datos de pacientes.',
        location: null,
        speaker: 'Dr. Jorge Fonseca',
        mode: 'Enlace Virtual (Teams)',
        price: 'Gratuito',
        cupos: null,
        image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&q=80',
        color: '#22c55e',
    },
    {
        id: 6,
        type: 'Congreso',
        title: 'Neurociencia Computacional: Desafíos 2025',
        date: '10 Ene, 2025',
        description: 'Revisión de modelos de conectoma, simulaciones de redes neuronales y aplicaciones en diagnóstico psiquiátrico.',
        location: 'Barcelona, España',
        speaker: 'Dra. Lucía Moreno',
        mode: null,
        price: null,
        cupos: 'Cupos Limitados',
        image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&q=80',
        color: '#FF6600',
    },
];

const TYPES = ['Todos', 'Congreso', 'Webinar', 'Simposio', 'Taller'];

const TYPE_ICONS = {
    Todos: <FaCalendarAlt />,
    Congreso: <FaUsers />,
    Webinar: <FaComments />,
    Simposio: <FaStar />,
    Taller: <FaTools />,
};

const ITEMS_PER_PAGE = 3;

// ── NOTIFICATION MOCK DATA ─────────────────────────────────────────────────
const INITIAL_NOTIFICATIONS = [];

const NOTIF_COLORS = {
    urgent: { bg: 'rgba(239,68,68,0.12)', border: '#ef4444', icon: <FaExclamationTriangle style={{ color: '#ef4444' }} /> },
    warning: { bg: 'rgba(245,158,11,0.12)', border: '#f59e0b', icon: <FaClock style={{ color: '#f59e0b' }} /> },
    info: { bg: 'rgba(59,130,246,0.10)', border: '#3b82f6', icon: <FaInfoCircle style={{ color: '#3b82f6' }} /> },
};

// ── Notification Dropdown ─────────────────────────────────────────────────
const NotificationPanel = ({ notifications, onDismiss, onMarkAll, onClose }) => {
    const unreadCount = notifications.filter(n => !n.read).length;

    return (
        <div className="se-notif-panel">
            <div className="se-notif-header">
                <span className="se-notif-title">Notificaciones</span>
                {unreadCount > 0 && (
                    <span className="se-notif-badge-count">{unreadCount} nuevas</span>
                )}
                <div className="se-notif-header-actions">
                    <button className="se-notif-mark-all" onClick={onMarkAll} title="Marcar todas como leídas">
                        <FaCheckDouble /> Leídas
                    </button>
                    <button className="se-notif-close" onClick={onClose}><FaTimes /></button>
                </div>
            </div>

            <div className="se-notif-list">
                {notifications.length === 0 ? (
                    <p className="se-notif-empty">No hay notificaciones</p>
                ) : (
                    notifications.map(n => {
                        const style = NOTIF_COLORS[n.level];
                        return (
                            <div
                                key={n.id}
                                className={`se-notif-item ${n.read ? 'read' : ''}`}
                                style={{ background: n.read ? 'transparent' : style.bg, borderLeft: `3px solid ${style.border}` }}
                            >
                                <div className="se-notif-icon">{style.icon}</div>
                                <div className="se-notif-body">
                                    <p className="se-notif-item-title">{n.title}</p>
                                    <p className="se-notif-item-msg">{n.message}</p>
                                    <span className="se-notif-time">{n.time}</span>
                                </div>
                                <button className="se-notif-dismiss" onClick={() => onDismiss(n.id)} title="Descartar">
                                    <FaTimes />
                                </button>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

// ── Mini Calendar ──────────────────────────────────────────────────────────
const DAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const MiniCalendar = ({ events = [] }) => {
    const today = new Date(); 
    const [viewDate, setViewDate] = useState(today);

    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    // adjust so week starts on Monday
    const startOffset = (firstDay === 0 ? 6 : firstDay - 1);
    const cells = Array(startOffset).fill(null).concat(Array.from({ length: daysInMonth }, (_, i) => i + 1));

    const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
    const nextMonth = () => setViewDate(new Date(year, month + 1, 1));

    // highlight days with events that match the current month and year
    const eventDays = useMemo(() => {
        const days = new Set();
        const currentMonthStr = MONTHS[month].toLowerCase();
        const currentYearStr = year.toString();

        events.forEach(e => {
            const dateStr = (e.fecha_inicio || e.date || '').toLowerCase();
            if (!dateStr) return;
            
            // simple check: does the date string contain the current month and year?
            // also handle "Nov" vs "Noviembre" and "Dic" vs "Diciembre" from mock data
            const shortMonth = currentMonthStr.substring(0, 3);
            const matchesMonth = dateStr.includes(currentMonthStr) || dateStr.includes(shortMonth);
            const matchesYear = dateStr.includes(currentYearStr);

            if (matchesMonth && matchesYear) {
                // Try to extract numbers from strings like "15 de Octubre de 2026" or "15 Nov, 2024"
                const dayMatch = dateStr.match(/\b(\d{1,2})\b/);
                if (dayMatch) {
                    days.add(parseInt(dayMatch[1], 10));
                }
            }
        });
        return days;
    }, [events, month, year]);

    return (
        <div className="se-calendar">
            <div className="se-cal-header">
                <button className="se-cal-nav" onClick={prevMonth}><FaChevronLeft /></button>
                <span className="se-cal-title">{MONTHS[month]} {year}</span>
                <button className="se-cal-nav" onClick={nextMonth}><FaChevronRight /></button>
            </div>
            <div className="se-cal-grid">
                {DAYS.map((d, i) => (
                    <span key={i} className="se-cal-dow">{d}</span>
                ))}
                {cells.map((day, i) => (
                    <span
                        key={i}
                        className={`se-cal-day ${day === today.getDate() && month === today.getMonth() ? 'today' : ''} ${day && eventDays.has(day) ? 'has-event' : ''} ${!day ? 'empty' : ''}`}
                    >
                        {day || ''}
                    </span>
                ))}
            </div>
        </div>
    );
};

// ── Event Card ─────────────────────────────────────────────────────────────
const EventCard = ({ event }) => {
    const [actionState, setActionState] = useState(null); // 'reserved' | 'alert' | null

    // Determine color based on event type
    const getTypeColor = (type) => {
        if (!type) return '#FF6600';
        const t = String(type).toLowerCase();
        if (t.includes('webinar')) return '#22c55e';
        if (t.includes('taller')) return '#f59e0b';
        if (t.includes('simposio')) return '#a855f7';
        return '#FF6600'; // congreso or default
    };
    
    const typeColor = event.color || getTypeColor(event.tipo_evento || event.type);
    
    // Parse types if it's an array or string
    let typeDisplay = event.tipo_evento || event.type || 'Evento';
    if (Array.isArray(typeDisplay)) typeDisplay = typeDisplay[0] || 'Evento';
    
    const dateDisplay = event.fecha_inicio && event.fecha_fin 
        ? `${event.fecha_inicio} - ${event.fecha_fin}` 
        : event.fecha_inicio || event.fecha_fin || event.date || 'Por confirmar';

    return (
        <div className="se-event-card">
            <div className="se-event-img-wrap">
                <img src={event.image || 'https://images.unsplash.com/photo-1530026405186-ed1f139313f3?w=400&q=80'} alt={event.nombre || event.title} className="se-event-img" loading="lazy" />
            </div>
            <div className="se-event-body">
                <div className="se-event-meta-top">
                    <span className="se-type-badge" style={{ borderColor: typeColor, color: typeColor, textTransform: 'capitalize' }}>
                        {typeDisplay}
                    </span>
                    <span className="se-event-date"><FaCalendarAlt /> {dateDisplay}</span>
                </div>
                <h3 className="se-event-title">{event.nombre || event.title}</h3>
                <p className="se-event-desc">{event.descripcion || event.description}</p>
                <div className="se-event-details">
                    {(event.pais_region || event.location) && (
                        <span className="se-detail"><FaMapMarkerAlt /> {event.pais_region || event.location}</span>
                    )}
                    {(event.organizacion || event.speaker) && (
                        <span className="se-detail"><FaUser /> {event.organizacion || event.speaker}</span>
                    )}
                    {(event.modalidad || event.mode) && (
                        <span className="se-detail"><FaLink /> {event.modalidad || event.mode}</span>
                    )}
                    {event.price && (
                        <span className="se-detail"><FaDollarSign /> {event.price}</span>
                    )}
                    {event.cupos && (
                        <span className="se-detail cupos"><FaTag /> {event.cupos}</span>
                    )}
                </div>
                <div className="se-event-actions" style={{ display: 'flex', gap: '8px', marginTop: '1rem', flexWrap: 'wrap' }}>
                    <a href={event.link || '#'} target="_blank" rel="noopener noreferrer" className="se-inscribe-btn" style={{textDecoration: 'none', display: 'inline-block', textAlign: 'center', flex: 1, minWidth: '120px'}}>Ver Oficial</a>
                    
                    <button 
                        onClick={() => setActionState(actionState === 'reserved' ? null : 'reserved')}
                        className={`se-inscribe-btn ${actionState === 'reserved' ? 'active' : ''}`}
                        style={{ 
                            flex: 1, minWidth: '120px', 
                            backgroundColor: actionState === 'reserved' ? '#22c55e' : 'transparent',
                            color: actionState === 'reserved' ? '#fff' : '#22c55e',
                            border: '1px solid #22c55e'
                        }}
                    >
                        {actionState === 'reserved' ? <><FaCheckDouble /> Reservado</> : <><FaCalendarAlt /> Agendar</>}
                    </button>

                    <button 
                        onClick={() => setActionState(actionState === 'alert' ? null : 'alert')}
                        className={`se-inscribe-btn ${actionState === 'alert' ? 'active' : ''}`}
                        style={{ 
                            flex: 1, minWidth: '120px', 
                            backgroundColor: actionState === 'alert' ? '#f59e0b' : 'transparent',
                            color: actionState === 'alert' ? '#fff' : '#f59e0b',
                            border: '1px solid #f59e0b'
                        }}
                        title="Alertarme con anterioridad"
                    >
                        {actionState === 'alert' ? <><FaBell /> Alerta Activa</> : <><FaBell /> Recordarme</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Main Component ─────────────────────────────────────────────────────────
const ScientificEvents = () => {
    const navigate = useNavigate();
    const [activeType, setActiveType] = useState('Todos');
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const [dbEvents, setDbEvents] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(true);
    const [notifications, setNotifications] = useState(INITIAL_NOTIFICATIONS);
    const [notifOpen, setNotifOpen] = useState(false);
    const notifRef = useRef(null);

    // Fetch Events from DB
    useEffect(() => {
        const fetchEvents = async () => {
            try {
                setLoadingEvents(true);
                const response = await axios.get('http://localhost:5000/api/events', {
                    params: { limit: 100 }
                });
                if (response.data && response.data.events) {
                    const fetchedEvents = response.data.events;
                    setDbEvents(fetchedEvents);

                    // Build dynamic notifications from DB events
                    const dynamicNotifs = fetchedEvents.slice(0, 5).map((e, index) => {
                        return {
                            id: `notif-${e.id}`,
                            level: index === 0 ? 'urgent' : (index % 2 === 0 ? 'warning' : 'info'),
                            eventId: e.id,
                            title: index === 0 ? '⚡ ¡Evento destacado!' : '📅 Próximo Evento',
                            message: `"${e.nombre}" está programado para ${e.fecha_inicio}.`,
                            time: 'Reciente',
                            read: false,
                        };
                    });
                    setNotifications(dynamicNotifs);
                }
            } catch (err) {
                console.error("Error fetching events", err);
            } finally {
                setLoadingEvents(false);
            }
        };
        fetchEvents();
    }, []);

    // Close panel on outside click
    useEffect(() => {
        const handler = (e) => {
            if (notifRef.current && !notifRef.current.contains(e.target)) {
                setNotifOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const unreadCount = notifications.filter(n => !n.read).length;

    const dismissNotif = (id) => setNotifications(prev => prev.filter(n => n.id !== id));
    const markAllRead = () => setNotifications(prev => prev.map(n => ({ ...n, read: true })));

    const filtered = useMemo(() => {
        let result = dbEvents.length > 0 ? dbEvents : EVENTS;
        if (activeType !== 'Todos') {
            result = result.filter(e => {
                const type = (e.tipo_evento || e.type || '').toLowerCase();
                return type.includes(activeType.toLowerCase());
            });
        }
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(e => {
                const title = (e.nombre || e.title || '').toLowerCase();
                const desc = (e.descripcion || e.description || '').toLowerCase();
                const loc = (e.pais_region || e.location || '').toLowerCase();
                return title.includes(q) || desc.includes(q) || loc.includes(q);
            });
        }
        return result;
    }, [activeType, searchQuery, dbEvents]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
    const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

    const handleTypeChange = (type) => {
        setActiveType(type);
        setPage(1);
    };

    const nextProximo = dbEvents.length > 0 ? dbEvents[0] : EVENTS[0]; // first event as "próximo"

    return (
        <div className="se-page">
            {/* ── HEADER ── */}
            <header className="se-header">
                <div className="se-header-inner">
                    <Link to="/" className="se-logo">
                        <img src="/logoecseia.png" alt="Logo" className="se-logo-img" />
                        <span className="se-logo-eco">Eco</span>
                        <span className="se-logo-rest">ScienceInnova</span>
                    </Link>
                    <div className="se-header-search">
                        <FaSearch className="se-header-search-icon" />
                        <input
                            className="se-header-search-input"
                            placeholder="Buscar eventos..."
                            value={searchQuery}
                            onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
                        />
                    </div>
                    <div className="se-header-actions">
                        <div className="se-bell-wrap" ref={notifRef}>
                            <button
                                className="se-header-icon-btn"
                                onClick={() => setNotifOpen(o => !o)}
                                aria-label="Notificaciones"
                            >
                                <FaBell />
                                {unreadCount > 0 && (
                                    <span className="se-bell-badge">{unreadCount}</span>
                                )}
                            </button>
                            {notifOpen && (
                                <NotificationPanel
                                    notifications={notifications}
                                    onDismiss={dismissNotif}
                                    onMarkAll={markAllRead}
                                    onClose={() => setNotifOpen(false)}
                                />
                            )}
                        </div>
                        <div className="se-header-avatar"><FaUserCircle /></div>
                    </div>
                </div>
            </header>

            {/* ── PAGE TITLE ── */}
            <div className="se-title-bar">
                <div className="se-title-bar-inner">
                    <div>
                        <h1 className="se-page-title">Agenda Científica</h1>
                        <p className="se-page-subtitle">Explora congresos, webinars y simposios de vanguardia.</p>
                    </div>
                    <button className="se-agenda-btn" onClick={() => navigate('/personal-agenda')}>
                        <FaCalendarAlt /> Mi Agenda
                    </button>
                </div>
            </div>

            {/* ── FILTER TABS ── */}
            <div className="se-filters-bar">
                <div className="se-filters-inner">
                    {TYPES.map(type => (
                        <button
                            key={type}
                            className={`se-filter-btn ${activeType === type ? 'active' : ''}`}
                            onClick={() => handleTypeChange(type)}
                        >
                            {TYPE_ICONS[type]} {type}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── BODY ── */}
            <div className="se-body">
                {/* Left sidebar */}
                <aside className="se-sidebar">
                    <MiniCalendar events={dbEvents.length > 0 ? dbEvents : EVENTS} />

                    <div className="se-proximo-card">
                        <div className="se-proximo-header">
                            <span className="se-proximo-dot">🟡</span>
                            <span className="se-proximo-label">Próximo Evento</span>
                        </div>
                        <h4 className="se-proximo-title">{nextProximo.nombre || nextProximo.title}</h4>
                        <p className="se-proximo-countdown">Empieza pronto.</p>
                        <a href={nextProximo.link || '#'} target="_blank" rel="noopener noreferrer" className="se-ver-detalles-btn" style={{textDecoration: 'none', display: 'inline-block', textAlign: 'center'}}>Ver detalles</a>
                    </div>
                </aside>

                {/* Events list */}
                <main className="se-events-list">
                    {loadingEvents ? (
                        <div className="se-empty">Cargando eventos desde la base de datos...</div>
                    ) : paginated.length === 0 ? (
                        <div className="se-empty">No se encontraron eventos para esta búsqueda.</div>
                    ) : (
                        paginated.map(event => <EventCard key={event.id} event={event} />)
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="se-pagination">
                            {page > 1 && (
                                <button className="se-page-btn" onClick={() => setPage(p => p - 1)}>
                                    <FaChevronLeft />
                                </button>
                            )}
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                                <button
                                    key={n}
                                    className={`se-page-btn ${n === page ? 'active' : ''}`}
                                    onClick={() => setPage(n)}
                                >
                                    {n}
                                </button>
                            ))}
                            {page < totalPages && (
                                <button className="se-page-btn" onClick={() => setPage(p => p + 1)}>
                                    <FaChevronRight />
                                </button>
                            )}
                        </div>
                    )}
                </main>
            </div>

            {/* ── FOOTER ── */}
            <footer className="se-footer">
                <div className="se-footer-inner">
                    <Link to="/" className="se-footer-logo">
                        <img src="/logoecseia.png" alt="Logo" className="se-footer-logo-img" />
                        <span><span className="se-logo-eco">Eco</span>ScienceInnova</span>
                    </Link>
                    <div className="se-footer-links">
                        <a href="#privacidad">Privacidad</a>
                        <a href="#terminos">Términos de Uso</a>
                        <a href="#contacto">Contacto</a>
                        <a href="#soporte">Soporte</a>
                    </div>
                    <button className="se-footer-share"><FaLink /></button>
                </div>
                <p className="se-footer-copy">© 2024 EcoScienceInnova. Investigación para un Futuro Sostenible.</p>
            </footer>
        </div>
    );
};

export default ScientificEvents;
